import {
  collection, doc, getDoc, getDocs, setDoc, addDoc,
  updateDoc, deleteDoc, query, where, limit, orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ---- User / Owner Profiles ----

export const getUserProfile = async (uid, preferredType = 'user') => {
  const primary = preferredType === 'owner' ? 'owners' : 'users';
  const snap = await getDoc(doc(db, primary, uid));
  if (snap.exists()) return { id: snap.id, ...snap.data(), type: preferredType === 'owner' ? 'owner' : 'user' };

  const other = preferredType === 'owner' ? 'users' : 'owners';
  const snap2 = await getDoc(doc(db, other, uid));
  if (snap2.exists()) return { id: snap2.id, ...snap2.data(), type: preferredType === 'owner' ? 'user' : 'owner' };

  return null;
};

export const createUserProfile = async (uid, userData, type = 'user') => {
  const col = type === 'owner' ? 'owners' : 'users';
  const today = new Date().toISOString().split('T')[0];
  const data = {
    email: userData.email,
    name: userData.name,
    photoURL: userData.photoURL || '',
    createdAt: today,
    lastLogin: today,
  };
  await setDoc(doc(db, col, uid), data);
  return { id: uid, ...data, type };
};

export const updateLastLogin = async (uid, type = 'user') => {
  const col = type === 'owner' ? 'owners' : 'users';
  await updateDoc(doc(db, col, uid), { lastLogin: new Date().toISOString().split('T')[0] });
};

// ---- Establishments ----

export const getOwnerEstablishments = async (ownerId) => {
  const q = query(collection(db, 'establishments'), where('ownerId', '==', ownerId));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const getEstablishmentById = async (id) => {
  const snap = await getDoc(doc(db, 'establishments', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createEstablishment = async (ownerId, data) => {
  const ref = await addDoc(collection(db, 'establishments'), {
    ownerId,
    name: data.name,
    logoUrl: data.logoUrl || '',
    themeColor: data.themeColor || '#F59E0B',
    rewardExpirationDays: Number(data.rewardExpirationDays) || 30,
    createdAt: new Date().toISOString(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
};

export const deleteEstablishment = async (id) => {
  const game = await getGameByEstablishment(id);
  if (game) {
    await deletePrizesByGameId(game.id);
    await deleteDoc(doc(db, 'games', game.id));
  }
  await deleteDoc(doc(db, 'establishments', id));
};

// ---- Games ----

export const getGameByEstablishment = async (establishmentId) => {
  const q = query(collection(db, 'games'), where('establishmentId', '==', establishmentId), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const createGame = async (establishmentId, data = {}) => {
  const ref = await addDoc(collection(db, 'games'), {
    establishmentId,
    name: data.name || 'Wheel of Fortune',
    type: 'spinning-wheel',
    active: true,
    createdAt: new Date().toISOString(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
};

export const getOrCreateGame = async (establishmentId) => {
  const existing = await getGameByEstablishment(establishmentId);
  if (existing) return existing;
  return createGame(establishmentId);
};

// ---- Prizes ----

export const getPrizesByGameId = async (gameId) => {
  const q = query(collection(db, 'prizes'), where('gameId', '==', gameId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createPrize = async (prizeData) => {
  const ref = await addDoc(collection(db, 'prizes'), {
    gameId: prizeData.gameId,
    name: prizeData.name,
    probability: prizeData.probability,
    isNoReward: prizeData.isNoReward || false,
    backgroundColor: prizeData.backgroundColor || '#FBBF24',
    textColor: prizeData.textColor || '#000000',
    timesWon: 0,
    createdAt: new Date().toISOString(),
  });
  const snap = await getDoc(ref);
  return { id: snap.id, ...snap.data() };
};

export const updatePrize = async (prizeId, data) => {
  await updateDoc(doc(db, 'prizes', prizeId), {
    name: data.name,
    probability: data.probability,
    isNoReward: data.isNoReward || false,
    backgroundColor: data.backgroundColor,
    textColor: data.textColor,
  });
};

export const deletePrize = async (prizeId) => {
  await deleteDoc(doc(db, 'prizes', prizeId));
};

export const deletePrizesByGameId = async (gameId) => {
  const prizes = await getPrizesByGameId(gameId);
  await Promise.all(prizes.map(p => deleteDoc(doc(db, 'prizes', p.id))));
};

// ---- Plays / Rewards ----

const generateCouponCode = () => Math.floor(1000 + Math.random() * 9000).toString();

export const checkUserSpinEligibility = async (userId, establishmentId) => {
  return true; // TODO: re-enable daily limit before going live
  // const snap = await getDoc(doc(db, 'users', userId));
  // if (!snap.exists()) return true;
  // const lastSpins = snap.data().lastSpins || {};
  // const lastSpin = lastSpins[establishmentId];
  // if (!lastSpin) return true;
  // const yesterday = new Date();
  // yesterday.setDate(yesterday.getDate() - 1);
  // return new Date(lastSpin) < yesterday;
};

export const recordSpin = async (userId, establishmentId, prize, userInfo = {}) => {
  const establishment = await getEstablishmentById(establishmentId);
  const expirationDays = establishment?.rewardExpirationDays || 30;
  const now = new Date();
  const isNoReward = prize.isNoReward || false;

  let expiresAt = null;
  if (!isNoReward) {
    const d = new Date(now);
    d.setDate(d.getDate() + expirationDays);
    expiresAt = d.toISOString().split('T')[0];
  }

  const couponCode = isNoReward ? null : generateCouponCode();
  const playedAt = now.toISOString().split('T')[0];

  const playData = {
    userId,
    userName: userInfo.name || '',
    userEmail: userInfo.email || '',
    establishmentId,
    establishmentName: establishment?.name || '',
    prizeId: prize.id,
    prizeName: prize.name,
    isNoReward,
    couponCode,
    playedAt,
    expiresAt,
    isClaimed: false,
  };

  const ref = await addDoc(collection(db, 'plays'), playData);

  try {
    await updateDoc(doc(db, 'users', userId), {
      [`lastSpins.${establishmentId}`]: now.toISOString(),
    });
  } catch (e) { /* non-critical */ }

  if (prize.id && !isNoReward) {
    try {
      const prizeRef = doc(db, 'prizes', prize.id);
      const prizeSnap = await getDoc(prizeRef);
      if (prizeSnap.exists()) {
        await updateDoc(prizeRef, { timesWon: (prizeSnap.data().timesWon || 0) + 1 });
      }
    } catch (e) { /* non-critical */ }
  }

  return { id: ref.id, ...playData };
};

// ---- Owner Analytics / Customer Management ----

export const getEstablishmentPlays = async (establishmentId) => {
  const q = query(
    collection(db, 'plays'),
    where('establishmentId', '==', establishmentId),
    orderBy('playedAt', 'desc'),
    limit(500)
  );
  try {
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch {
    // Fallback if composite index not yet created
    const q2 = query(collection(db, 'plays'), where('establishmentId', '==', establishmentId), limit(500));
    const snap = await getDocs(q2);
    const plays = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    plays.sort((a, b) => (b.playedAt || '').localeCompare(a.playedAt || ''));
    return plays;
  }
};

export const getEstablishmentPlayersWithHistory = async (establishmentId) => {
  const plays = await getEstablishmentPlays(establishmentId);
  const userIds = [...new Set(plays.map(p => p.userId))];

  // Try to enrich with user profiles (best-effort — may be denied by rules)
  const userProfiles = {};
  await Promise.all(userIds.map(async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) userProfiles[uid] = snap.data();
    } catch { /* permission denied — fall back to play data */ }
  }));

  const playerMap = {};
  plays.forEach(play => {
    if (!playerMap[play.userId]) {
      const profile = userProfiles[play.userId] || {};
      // Use user profile if available, fall back to data stored in the play record
      playerMap[play.userId] = {
        userId: play.userId,
        name: profile.name || play.userName || 'Unknown',
        email: profile.email || play.userEmail || '',
        photoURL: profile.photoURL || '',
        plays: [],
      };
    }
    playerMap[play.userId].plays.push(play);
  });

  return Object.values(playerMap).sort((a, b) => {
    const la = a.plays[0]?.playedAt || '';
    const lb = b.plays[0]?.playedAt || '';
    return lb.localeCompare(la);
  });
};

export const markPlayAsClaimed = async (playId) => {
  await updateDoc(doc(db, 'plays', playId), { isClaimed: true });
};

// ---- Establishment Settings ----

export const updateEstablishment = async (id, data) => {
  await updateDoc(doc(db, 'establishments', id), data);
};

export const getEstablishmentsByIds = async (ids) => {
  if (!ids.length) return [];
  const snaps = await Promise.all(ids.map(id => getDoc(doc(db, 'establishments', id))));
  return snaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() }));
};

// ---- Bonus Spins (Win-Back) ----

export const createBonusSpin = async (userId, establishmentId) => {
  const token =
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36);
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);
  await setDoc(doc(db, 'bonusSpins', token), {
    userId,
    establishmentId,
    createdAt: new Date().toISOString(),
    expiresAt: expiresAt.toISOString(),
    used: false,
  });
  return token;
};

export const consumeBonusSpin = async (token) => {
  const snap = await getDoc(doc(db, 'bonusSpins', token));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.used || new Date(data.expiresAt) < new Date()) return null;
  await updateDoc(doc(db, 'bonusSpins', token), { used: true });
  return { userId: data.userId, establishmentId: data.establishmentId };
};

// ---- Stamp Card ----

export const getUserSpinCount = async (userId, establishmentId) => {
  const q = query(
    collection(db, 'plays'),
    where('userId', '==', userId),
    where('establishmentId', '==', establishmentId)
  );
  const snap = await getDocs(q);
  return snap.size;
};

export const getPendingGuaranteedSpins = async (userId, establishmentId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) return 0;
  return snap.data().guaranteedSpins?.[establishmentId] || 0;
};

export const awardGuaranteedSpin = async (userId, establishmentId) => {
  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);
  const current = snap.exists() ? (snap.data().guaranteedSpins?.[establishmentId] || 0) : 0;
  await updateDoc(userRef, { [`guaranteedSpins.${establishmentId}`]: current + 1 });
};

export const consumeGuaranteedSpin = async (userId, establishmentId) => {
  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return false;
  const current = snap.data().guaranteedSpins?.[establishmentId] || 0;
  if (current <= 0) return false;
  await updateDoc(userRef, { [`guaranteedSpins.${establishmentId}`]: current - 1 });
  return true;
};

export const getUserRewards = async (userId) => {
  const q = query(collection(db, 'plays'), where('userId', '==', userId));
  const snap = await getDocs(q);
  const plays = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  plays.sort((a, b) => new Date(b.playedAt) - new Date(a.playedAt));

  return plays.map(play => {
    let daysUntilExpiration = null;
    let isExpired = false;
    if (play.expiresAt) {
      const exp = new Date(play.expiresAt);
      const today = new Date();
      exp.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      daysUntilExpiration = Math.ceil((exp - today) / 86400000);
      isExpired = daysUntilExpiration < 0;
    }
    return { ...play, daysUntilExpiration, isExpired };
  });
};
